// hooks/useUser.js
import { useAtom } from 'jotai'
import { useEffect } from 'react'

import { userReadonlyAtom, userLoadingAtomFamily, usersAtom, loadingUsersAtom } from '../../domains/user/AtomUsers'

export function useUser(userId) {
  const [user] = useAtom(userReadonlyAtom(userId))
  const [isLoading, setIsLoading] = useAtom(userLoadingAtomFamily(userId))

  const unidentifiedUser = userId === null || userId === undefined || userId === ''

  useEffect(() => {
    if (unidentifiedUser || isLoading) return
    if (!user && !isLoading) {
      setIsLoading(true)
    }
  }, [userId, user, isLoading, unidentifiedUser, setIsLoading])

  if (!userId) {
    return { user: null, isLoading: false }
  }

  return { user, isLoading }
}

export function useUsers(userIdsList) {
  const [users] = useAtom(usersAtom)
  const [loadingSet, setLoadingSet] = useAtom(loadingUsersAtom)

  const userIds = userIdsList?.length > 0 ? userIdsList : []

  // a list of unique ids that are not in users
  const missingIds = [...new Set(userIds?.filter((id) => !users[id]))]
  // a list of ids that are loading
  const isLoading = userIds?.some((id) => loadingSet.has(id))
  // Get a list of ids that are missing but not loading
  const missingIdsNotLoading = missingIds?.filter((id) => !loadingSet.has(id))

  useEffect(() => {
    if (missingIdsNotLoading.length === 0) return

    setLoadingSet((prev) => {
      const next = new Set(prev)
      missingIdsNotLoading.forEach((id) => next.add(id))
      return next
    })
  }, [missingIdsNotLoading, setLoadingSet])

  // 返回所需用户数据
  const usersArr = userIds.map((id) => users[id] || null).filter((user) => user !== null)

  return {
    users: usersArr,
    isLoading,
  }
}

export function useParticipants(participants) {
  // Sanitize participants to ensure unique (id, familyId) pairs
  const sanitizedParticipants = Array.from(
    new Map(
      participants.map((participant) => {
        if (typeof participant === 'string') {
          // For string, treat participant id as string and familyId default as 0
          return [`${participant}_0`, { id: participant, familyId: 0 }]
        }
        // For object, compose unique key with id and familyId
        const id = participant.id
        const familyId = participant.familyId || 0
        return [`${id}_${familyId}`, { id, familyId }]
      }),
    ).values(),
  )

  const participantIdsSet = new Set(sanitizedParticipants.map((p) => p.id))
  const { users, isLoading } = useUsers(Array.from(participantIdsSet))

  const usersWithBadge = sanitizedParticipants
    .filter((p) => {
      return users.some((u) => u.resourceId === p.id)
    })
    .map((p) => {
      const user = users.find((u) => u.resourceId === p.id)
      const familyMember = user?.familyMembers?.find((m) => m.familyId === p.familyId)
      return { ...user, familyId: p.familyId || 0, name: familyMember?.name || user?.name }
    })

  return {
    users: usersWithBadge,
    isLoading,
  }
}
